import { useCallback, useEffect, useState } from 'react';

import { PropTypes } from 'prop-types';

import emptyDataUrl from 'components/ui/svg/undraw_empty_xct9.svg';

import { accentInsensitiveSearch } from 'utils/helpers';
import { useDebounce } from 'utils/hooks';

import ActionBar from './ActionBar';
import ManagementList from './ManagementList';
import {
  ManagementListItems,
  ManagementPagination,
  sortItems,
} from './ManagementListComponent';
import HeaderFactory from './header';

const defaultPageSize = 20;
const defaultPage = 1;

function PaginatedManagementList({
  items,
  testid,
  compact,
  actions,
  nActions,
  rowFields,
  fetchItems,
  updateItems,
  showArchived,
  rowIdAccessor,
  defaultSorted,
  renderItemRow,
  withTextFilter,
  setShowArchived,
  getTextFromItem,
  EmptyListContent,
  hasLockedProperty,
  textFilterPlaceholder,
  ...props
}) {
  const [filteredItems, setFilteredItems] = useState(items);
  const [columnSorted, setColumnSorted] = useState(defaultSorted);
  const [currentPage, setCurrentPage] = useState(defaultPage);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [facetSearchValue, setFacetSearchValue] = useState('');

  const fetchData = useCallback(
    async (page, pageSize, sortField, sortOrder, query) => {
      try {
        setLoading(true);
        const effectivePage = page === 0 ? 1 : page;
        const response = await fetchItems({
          page: effectivePage,
          pageSize,
          sortField,
          sortOrder,
          query,
          showArchived,
        });
        const filteredResponseItems = showArchived
          ? response.items.filter((item) => item.archived === true)
          : response.items;
        setFilteredItems(filteredResponseItems);
        setTotalPages(Math.ceil(response.count / defaultPageSize));
      } catch (error) {
        setFilteredItems([]);
      } finally {
        setLoading(false);
      }
    },
    [currentPage, showArchived]
  );

  const onResetAllFilters = useCallback(() => {
    setFacetSearchValue('');
  }, [items, columnSorted, setFacetSearchValue]);

  const onItemSort = useCallback(
    (fieldName) => () => {
      const newFacetSorted = {
        key: fieldName,
        ascending:
          columnSorted.key !== fieldName ||
          (columnSorted.key === fieldName && !columnSorted.ascending),
      };
      setColumnSorted(newFacetSorted);
      if (!fetchItems) {
        setFilteredItems((prevItems) =>
          sortItems(
            [...prevItems],
            newFacetSorted,
            rowFields.find(({ id }) => id === fieldName)?.accessor
          )
        );
      }
    },
    [items, columnSorted, filteredItems, setFilteredItems]
  );

  const onUpdateSearchResults = useCallback(
    (_e, { searchValue }) => {
      const sortField = rowFields.find(({ id }) => id === columnSorted?.key);
      if (fetchItems) {
        fetchData(
          currentPage,
          defaultPageSize,
          columnSorted.key,
          columnSorted.ascending ? 1 : -1,
          searchValue,
          showArchived
        );
      } else if (items && items.length) {
        const baseItems = [...items];
        const updatedFilteredItems = sortItems(
          searchValue
            ? accentInsensitiveSearch(baseItems, searchValue, getTextFromItem)
            : baseItems,
          columnSorted,
          sortField?.accessor,
          sortField?.sortType
        );
        setFilteredItems(updatedFilteredItems);
      } else {
        setFilteredItems([]);
      }
    },
    [items, columnSorted, showArchived, setFilteredItems]
  );

  const debouncedFacetSearchValue = useDebounce(
    facetSearchValue,
    fetchItems ? 500 : null
  );

  const onResetSearch = useCallback(() => {
    setFacetSearchValue('');
  }, [setFacetSearchValue]);

  const onSearchChange = useCallback(
    (_e, { value }) => {
      setFacetSearchValue(value);
    },
    [setFacetSearchValue]
  );

  const HeaderComponent = PaginatedManagementList.HeaderFactory([
    ...rowFields,
    { key: 'actions', width: 55 * nActions, auto: 'true' },
    { key: 'menu', width: 10 },
  ]);

  useEffect(() => {
    onUpdateSearchResults(null, { searchValue: debouncedFacetSearchValue });
  }, [debouncedFacetSearchValue, columnSorted, onUpdateSearchResults]);

  useEffect(() => {
    if (updateItems) setFilteredItems(updateItems);
  }, [items, updateItems, setFilteredItems]);

  const handlePageChange = useCallback(
    (_e, { activePage }) => {
      const effectivePage = activePage === 0 ? 1 : activePage;
      setCurrentPage(effectivePage);
      if (fetchItems) {
        fetchData(
          activePage,
          defaultPageSize,
          columnSorted.key,
          columnSorted.ascending ? 1 : -1,
          facetSearchValue
        );
      }
    },
    [
      fetchData,
      fetchItems,
      columnSorted.key,
      columnSorted.ascending,
      facetSearchValue,
    ]
  );

  return (
    <>
      <ActionBar
        textFilterPlaceholder={textFilterPlaceholder}
        onSearchChange={onSearchChange}
        onResetSearch={onResetSearch}
        facetSearchValue={facetSearchValue}
        testid={testid}
        actions={actions}
        withTextFilter={withTextFilter}
        showArchived={showArchived}
        setShowArchived={setShowArchived}
        hasLockedProperty={hasLockedProperty}
      />
      <ManagementListItems
        renderItemRow={renderItemRow}
        filteredItems={filteredItems}
        setFilteredItems={setFilteredItems}
        facetSearchValue={facetSearchValue}
        setFacetSearchValue={setFacetSearchValue}
        columnSorted={columnSorted}
        setColumnSorted={setColumnSorted}
        emptyDataUrl={emptyDataUrl}
        EmptyListContent={EmptyListContent}
        onResetAllFilters={onResetAllFilters}
        HeaderComponent={HeaderComponent}
        onItemSort={onItemSort}
        compact={compact}
        rowIdAccessor={rowIdAccessor}
        rowFields={rowFields}
        loading={loading}
        {...props}
      />
      <ManagementPagination
        fetchItems={fetchItems}
        totalPages={totalPages}
        currentPage={currentPage}
        loading={loading}
        handlePageChange={handlePageChange}
      />
    </>
  );
}

PaginatedManagementList.propTypes = {
  ...ManagementList.propTypes,
  fetchItems: PropTypes.func,
  hasLockedProperty: PropTypes.bool,
  renderItemRow: PropTypes.func.isRequired,
};

PaginatedManagementList.defaultProps = {
  ...ManagementList.defaultProps,
  fetchItems: null,
  hasLockedProperty: false,
};

PaginatedManagementList.HeaderFactory = HeaderFactory;

export default PaginatedManagementList;
